/**
 * career/analytics.ts — pure income intelligence + professional analytics.
 *
 * Everything derives from RECORDED income records, projects, and clients.
 * Missing data ⇒ honest nulls: hourly averages need recorded hours, trends
 * need ≥2 full months, stability ≥3, seasonality ≥12, forecasts ≥3 — and a
 * forecast is a labeled average-of-history projection, never a promise.
 */

import type {
  CareerClient,
  CareerProject,
  IncomeIntelligence,
  IncomeRecord,
  MonthlyEarnings,
  ProfessionalAnalytics,
} from './models';

function monthKey(at: number): string {
  const d = new Date(at);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

export function computeIncomeIntelligence(
  records: IncomeRecord[],
  currency: string,
  now: number,
): IncomeIntelligence {
  const inCurrency = records.filter((r) => r.currency === currency);
  const byMonth = new Map<string, MonthlyEarnings>();
  for (const r of inCurrency) {
    const key = monthKey(r.at);
    const m = byMonth.get(key) ?? { month: key, total: 0, recordCount: 0 };
    m.total += r.amount;
    m.recordCount += 1;
    byMonth.set(key, m);
  }
  const monthlyEarnings = Array.from(byMonth.values()).sort((a, b) => a.month.localeCompare(b.month));

  // Full months only for trend/stability/forecast — the current month is partial.
  const currentMonth = monthKey(now);
  const fullMonths = monthlyEarnings.filter((m) => m.month !== currentMonth);

  const withHours = inCurrency.filter((r) => r.hoursWorked != null && r.hoursWorked > 0);
  const totalHours = withHours.reduce((s, r) => s + r.hoursWorked!, 0);
  const hourlyAverage = totalHours > 0
    ? withHours.reduce((s, r) => s + r.amount, 0) / totalHours
    : null;

  let trend: IncomeIntelligence['trend'] = null;
  if (fullMonths.length >= 2) {
    const last = fullMonths[fullMonths.length - 1].total;
    const prev = fullMonths[fullMonths.length - 2].total;
    trend = prev === 0 ? (last > 0 ? 'rising' : 'flat')
      : Math.abs(last - prev) / prev <= 0.1 ? 'flat'
      : last > prev ? 'rising' : 'falling';
  }

  let stability: IncomeIntelligence['stability'] = null;
  if (fullMonths.length >= 3) {
    const totals = fullMonths.map((m) => m.total);
    const mean = totals.reduce((s, t) => s + t, 0) / totals.length;
    if (mean > 0) {
      const variance = totals.reduce((s, t) => s + (t - mean) ** 2, 0) / totals.length;
      stability = Math.sqrt(variance) / mean <= 0.35 ? 'stable' : 'volatile';
    }
  }

  let seasonalityNote: string | null = null;
  if (fullMonths.length >= 12) {
    const best = [...fullMonths].sort((a, b) => b.total - a.total)[0];
    seasonalityNote = `Strongest recorded month so far: ${best.month}.`;
  }

  const forecastNextMonth = fullMonths.length >= 3
    ? fullMonths.slice(-3).reduce((s, m) => s + m.total, 0) / 3
    : null;

  return {
    currency,
    totalRecorded: inCurrency.reduce((s, r) => s + r.amount, 0),
    monthlyEarnings,
    hourlyAverage,
    projectIncome: inCurrency.filter((r) => r.source === 'project').reduce((s, r) => s + r.amount, 0),
    missionIncome: inCurrency.filter((r) => r.source === 'mission').reduce((s, r) => s + r.amount, 0),
    trend,
    stability,
    seasonalityNote,
    forecastNextMonth,
  };
}

export function computeProfessionalAnalytics(
  projects: CareerProject[],
  clients: CareerClient[],
  incomeRecords: IncomeRecord[],
  now: number,
): ProfessionalAnalytics {
  const completed = projects.filter((p) => p.completedAt != null);

  const decided = projects.filter((p) => p.accepted != null);
  const acceptanceRate = decided.length > 0
    ? decided.filter((p) => p.accepted === true).length / decided.length
    : null;

  const withIncome = completed.filter((p) => p.income != null);
  const averageProjectEarnings = withIncome.length > 0
    ? withIncome.reduce((s, p) => s + p.income!, 0) / withIncome.length
    : null;

  const rated = clients.filter((c) => c.rating != null);
  const averageClientRating = rated.length > 0
    ? rated.reduce((s, c) => s + c.rating!, 0) / rated.length
    : null;

  const totalsByClient = new Map<string, number>();
  for (const r of incomeRecords) {
    if (r.clientId == null) continue;
    totalsByClient.set(r.clientId, (totalsByClient.get(r.clientId) ?? 0) + r.amount);
  }
  const bestPayingClients = Array.from(totalsByClient.entries())
    .map(([clientId, total]) => ({
      clientId,
      name: clients.find((c) => c.id === clientId)?.name ?? 'Unknown client',
      total,
    }))
    .sort((a, b) => b.total - a.total)
    .slice(0, 5);

  const totalsBySkill = new Map<string, number>();
  for (const p of withIncome) {
    for (const skill of p.skills) {
      totalsBySkill.set(skill, (totalsBySkill.get(skill) ?? 0) + p.income!);
    }
  }
  const mostProfitableSkills = Array.from(totalsBySkill.entries())
    .map(([skill, total]) => ({ skill, total }))
    .sort((a, b) => b.total - a.total)
    .slice(0, 5);

  let productivityTrend: ProfessionalAnalytics['productivityTrend'] = null;
  const key = (at: number) => {
    const d = new Date(at);
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
  };
  const currentMonth = key(now);
  const completionMonths = new Map<string, number>();
  for (const p of completed) {
    const k = key(p.completedAt!);
    if (k === currentMonth) continue; // partial month excluded
    completionMonths.set(k, (completionMonths.get(k) ?? 0) + 1);
  }
  const orderedMonths = Array.from(completionMonths.keys()).sort();
  if (orderedMonths.length >= 2) {
    const last = completionMonths.get(orderedMonths[orderedMonths.length - 1])!;
    const prev = completionMonths.get(orderedMonths[orderedMonths.length - 2])!;
    productivityTrend = last > prev ? 'more' : last < prev ? 'less' : 'steady';
  }

  return {
    projectsCompleted: completed.length,
    acceptanceRate,
    averageProjectEarnings,
    averageClientRating,
    ratedClientCount: rated.length,
    bestPayingClients,
    mostProfitableSkills,
    averageResponseTimeHours: null, // recorded-only; no integration records this yet
    productivityTrend,
  };
}
